import { graphql } from 'react-relay';

import { fg } from '@confluence/feature-gating';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	DataSecurityPolicyAction,
	useIsAttachmentDownloadDisabledQuery$data,
	useIsAttachmentDownloadDisabledQuery$variables,
	DataSecurityPolicyDecidableContentStatus,
} from './__generated__/useIsAttachmentDownloadDisabledQuery.graphql.ts';

interface UseIsAttachmentDownloadDisabledArgs {
	contentId: string;
	spaceKey?: string | null;
	skip?: boolean;
}

const useIsAttachmentDownloadDisabledQuery = graphql`
	query useIsAttachmentDownloadDisabledQuery(
		$action: DataSecurityPolicyAction!
		$contentId: ID!
		$contentStatus: DataSecurityPolicyDecidableContentStatus!
		$contentVersion: Int!
		$spaceKey: String
	) {
		dataSecurityPolicy {
			isActionEnabledForContent(
				action: $action
				contentId: $contentId
				contentStatus: $contentStatus
				contentVersion: $contentVersion
				spaceKey: $spaceKey
			) {
				action
				allowed
			}
		}
	}
`;

export const getAttachmentDSPQueryVariables = (
	action: DataSecurityPolicyAction,
	contentId: string,
	spaceKey?: string | null,
) => ({
	action,
	contentId,
	contentStatus: 'CURRENT' as DataSecurityPolicyDecidableContentStatus,
	contentVersion: 1,
	spaceKey,
});

export const useIsAttachmentDownloadDisabled = ({
	contentId,
	spaceKey,
	skip,
}: UseIsAttachmentDownloadDisabledArgs) => {
	const action = fg('cc-dlp_split-attachment-dl-from-export-dsp')
		? 'ATTACHMENT_DOWNLOAD'
		: 'PAGE_EXPORT';

	const { data, loading, error } = useQueryInterop<
		useIsAttachmentDownloadDisabledQuery$data,
		useIsAttachmentDownloadDisabledQuery$variables
	>(useIsAttachmentDownloadDisabledQuery, {
		variables: getAttachmentDSPQueryVariables(action, contentId, spaceKey),
		context: {
			allowOnExternalPage: true,
		},
		skip,
	});
	const allowedResp = data?.dataSecurityPolicy?.isActionEnabledForContent?.allowed;
	// Defaults to disabled if we don't have a definitive answer
	const isDisabled = allowedResp === undefined || allowedResp === null ? true : !allowedResp;
	return {
		isDisabled,
		loading,
		error,
	};
};
